import React, { useMemo } from 'react';
import { resolveElementStyle, buildFloatingMenuStyle } from '../../utils/mobileStyleUtils';

export default function MobileFloatingTabBar({
  activeTab,
  onTabChange,
  onSelectTab,
  cartCount: propCartCount,
  cartBadgeCount: propCartBadgeCount,
  user,
  mobileStyles,
  style: propStyle,
  onElementClick
}) {
  const handleTabSelect = onTabChange || onSelectTab;
  const badgeCount = propCartCount !== undefined ? propCartCount : (propCartBadgeCount || 0);

  const isOperator = Boolean(user && ['OPERATOR', 'TENANT_ADMIN', 'GLOBAL_ADMIN', 'GLOBAL_POWER', 'GLOBAL_STANDARD'].includes(user.role));

  const pageKey = isOperator ? `operator:${activeTab}` : `end_user:${activeTab}`;
  const menuConfig = useMemo(() => {
    if (!mobileStyles) return null;
    return resolveElementStyle(mobileStyles, pageKey, 'floating_menus').style;
  }, [mobileStyles, pageKey]);

  const customMenuStyle = useMemo(() => {
    if (!menuConfig) return null;
    return buildFloatingMenuStyle(menuConfig);
  }, [menuConfig]);

  const homeTab = {
    id: 'home',
    label: 'Home',
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.4' : '2'} strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    )
  };

  const vaultTab = {
    id: 'vault',
    label: 'Vault',
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.4' : '2'} strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="12" cy="12" r="5" />
        <path d="M12 7v2" /><path d="M12 15v2" /><path d="M7 12h2" /><path d="M15 12h2" />
      </svg>
    )
  };

  const shopTab = {
    id: 'shop',
    label: 'Shop',
    badge: badgeCount > 0 ? badgeCount : null,
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.4' : '2'} strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
    )
  };

  const claimTab = {
    id: 'claim',
    label: 'Claim',
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.4' : '2'} strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 2l-2 2m-1-1l-3 3m5 5l-2-2m-1 1l-3-3" />
        <path d="M15 5l4 4-8.5 8.5H6.5v-4L15 5z" />
        <circle cx="7" cy="17" r="2" />
      </svg>
    )
  };

  const profileTab = {
    id: 'profile',
    label: 'You',
    icon: (active) => {
      if (user?.profile_picture_url) {
        return (
          <img
            src={user.profile_picture_url}
            alt="Avatar"
            style={{
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              objectFit: 'cover',
              border: active ? '2px solid #00c6ff' : '1.5px solid rgba(255,255,255,0.4)'
            }}
          />
        );
      }
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.4' : '2'} strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    }
  };

  const tabs = isOperator
    ? [homeTab, vaultTab, profileTab]
    : [vaultTab, shopTab, claimTab, profileTab];

  return (
    <nav
      aria-label="Mobile Bottom Navigation"
      onClick={(e) => {
        if (onElementClick) {
          onElementClick('floating_menus');
        }
      }}
      style={{
        position: 'fixed',
        bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 32px)',
        maxWidth: '390px',
        height: '64px',
        backgroundColor: 'rgba(17, 24, 39, 0.88)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '9999px',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        boxShadow: '0 16px 40px rgba(0, 0, 0, 0.65), 0 0 1px rgba(255, 255, 255, 0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: '0 8px',
        zIndex: 9999,
        boxSizing: 'border-box',
        userSelect: 'none',
        ...(customMenuStyle || {}),
        ...(propStyle || {})
      }}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        const activeBg = menuConfig?.active_tab_bg || 'rgba(0, 198, 255, 0.14)';
        const activeColor = menuConfig?.active_tab_color || '#38bdf8';
        const inactiveColor = menuConfig?.inactive_tab_color || '#94a3b8';
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabSelect && handleTabSelect(tab.id)}
            data-testid={`mobile-tab-${tab.id}`}
            style={{
              position: 'relative',
              flex: 1,
              height: '50px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2px',
              background: isActive ? activeBg : 'transparent',
              border: 'none',
              borderRadius: menuConfig?.border_radius ? `${Math.min(menuConfig.border_radius, 50)}px` : '9999px',
              color: isActive ? activeColor : inactiveColor,
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              padding: '0 4px',
              outline: 'none',
              WebkitTapHighlightColor: 'transparent'
            }}
          >
            {/* Tab Icon with badge */}
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {tab.icon(isActive)}

              {/* Badge Counter (WhatsApp style) */}
              {tab.badge && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '-10px',
                    backgroundColor: '#10b981',
                    color: '#ffffff',
                    fontSize: '10px',
                    fontWeight: 800,
                    minWidth: '18px',
                    height: '18px',
                    borderRadius: '9999px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px',
                    boxShadow: '0 2px 6px rgba(16, 185, 129, 0.5)',
                    border: '1.5px solid #111827',
                    lineHeight: 1
                  }}
                >
                  {tab.badge}
                </span>
              )}
            </div>

            {/* Tab Label */}
            <span
              style={{
                fontSize: '10.5px',
                fontWeight: isActive ? 700 : 500,
                letterSpacing: '0.02em',
                transition: 'color 0.15s ease'
              }}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
